import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, AlertTriangle, Zap, Lock, ChevronRight, User, LogOut, Crown, BookOpen, Calculator, MessageSquare, Send, Download, Wallet, TrendingUp, Users, PiggyBank, Clock, ChevronDown, BarChart3, Target, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { InstallButton, DownloadAppSection, DownloadAppButton } from '@/components/InstallApp';
import axios from 'axios';
import { API } from '@/lib/api';
import PushNotifications from '@/components/PushNotifications';
import QuickNav from '@/components/QuickNav';
import OnboardingWizard from '@/components/OnboardingWizard';

import { BACKEND_URL } from '@/lib/api';

// Componente de botão com legenda rotativa
const NavButtonWithTooltip = ({ icon: Icon, label, tooltip, onClick, isActive, testId }) => {
  return (
    <div className="flex flex-col items-center relative">
      <Button 
        variant="ghost" 
        className="text-gray-400 hover:text-white text-sm px-2 sm:px-4"
        onClick={onClick}
        data-testid={testId}
      >
        <Icon className="w-4 h-4 sm:mr-2" />
        <span className="hidden sm:inline">{label}</span>
      </Button>
      
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.3 }}
            className="absolute top-full mt-1 flex flex-col items-center"
          >
            <span className={`text-sm ${tooltip.color}`}>▲</span>
            <span className={`text-[10px] sm:text-xs whitespace-nowrap ${tooltip.color} font-medium`}>
              {tooltip.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [suggestionText, setSuggestionText] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [sendingSuggestion, setSendingSuggestion] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [premiumInsights, setPremiumInsights] = useState([]);
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);

  // Check trial status and load premium insights for logged in users
  useEffect(() => {
    if (user) {
      checkTrialStatus();
    }
  }, [user]);

  // Abre o Wizard automaticamente para visitantes (não logados) ao acessar o site - TESTE
  useEffect(() => {
    if (!user) {
      const t = setTimeout(() => setShowWizard(true), 500);
      return () => clearTimeout(t);
    }
  }, [user]);

  // Load premium insights when user is premium
  useEffect(() => {
    if (trialInfo?.access_type === 'premium' && !trialInfo?.is_trial) {
      loadPremiumInsights();
    }
  }, [trialInfo]);

  // Rotate premium insights every 5 seconds
  useEffect(() => {
    if (premiumInsights.length > 1) {
      const interval = setInterval(() => {
        setCurrentInsightIndex((prev) => (prev + 1) % premiumInsights.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [premiumInsights]);

  const checkTrialStatus = async () => {
    try {
      const res = await axios.get(`${API}/expenses/access`, { withCredentials: true });
      setTrialInfo(res.data);
    } catch (error) {
      console.error('Error checking trial:', error);
    }
  };

  const loadPremiumInsights = async () => {
    try {
      const [comparisonRes, summaryRes] = await Promise.all([
        axios.get(`${API}/expenses/comparison`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/expenses/summary`, { withCredentials: true }).catch(() => null)
      ]);

      const insights = [];
      
      // Insight from comparison data
      if (comparisonRes?.data) {
        const { trend_percentage, trend_direction } = comparisonRes.data;
        if (trend_direction === 'down' && trend_percentage < 0) {
          insights.push(`Parabéns! Você economizou ${Math.abs(trend_percentage)}% em relação ao mês passado 🎉`);
        } else if (trend_direction === 'up' && trend_percentage > 0) {
          insights.push(`Seus gastos aumentaram ${trend_percentage}% este mês. Vamos reverter isso? 💪`);
        }
      }

      // Insight from summary data
      if (summaryRes?.data?.categories?.length > 0) {
        const topCategory = summaryRes.data.categories[0];
        insights.push(`Sua maior despesa este mês: ${topCategory.name} (${topCategory.percentage}% do total)`);
        
        // Check for categories under limit
        const underLimit = summaryRes.data.categories.filter(c => c.limit && !c.over_limit);
        if (underLimit.length > 0) {
          insights.push(`Você está dentro do limite em ${underLimit.length} categoria(s). Continue assim! ✨`);
        }

        // Check savings goal
        if (summaryRes.data.savings_goal && summaryRes.data.savings_status >= 0) {
          insights.push(`Você está dentro da sua meta de economia! Faltam R$ ${summaryRes.data.savings_status.toFixed(0)} para o limite 🎯`);
        }
      }

      // Default insight if no data
      if (insights.length === 0) {
        insights.push('Acesse o organizador para começar a registrar suas despesas!');
      }

      setPremiumInsights(insights);
    } catch (error) {
      console.error('Error loading insights:', error);
      setPremiumInsights(['Acesse o organizador para ver seus insights!']);
    }
  };

  // Rotação das tooltips: 3 segundos cada, depois pausa de 10 segundos
  useEffect(() => {
    if (isPaused) {
      // Pausa de 12 segundos após completar o ciclo
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setActiveTooltip(0);
      }, 12000);
      return () => clearTimeout(pauseTimer);
    } else {
      // Rotação normal de 3 segundos
      const interval = setInterval(() => {
        setActiveTooltip((prev) => {
          if (prev >= 3) {
            // Completou o ciclo, inicia pausa
            setIsPaused(true);
            return -1; // Esconde durante a pausa
          }
          return prev + 1;
        });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPaused]);

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Você precisa estar logado para enviar sugestões');
      login();
      return;
    }

    if (!suggestionText.trim() || suggestionText.trim().length < 10) {
      toast.error('A sugestão deve ter pelo menos 10 caracteres');
      return;
    }

    setSendingSuggestion(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ suggestion_text: suggestionText.trim() })
      });

      if (response.ok) {
        toast.success('Sugestão enviada com sucesso! Agradecemos seu feedback.');
        setSuggestionText('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao enviar sugestão');
      }
    } catch (error) {
      console.error('Error sending suggestion:', error);
      toast.error('Erro ao enviar sugestão. Tente novamente.');
    } finally {
      setSendingSuggestion(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer ml-12 sm:ml-14" onClick={() => navigate('/')}>
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-[56px] h-[56px] sm:w-24 sm:h-24 object-contain" />
              <span className="font-bold text-xl sm:text-2xl text-white hidden sm:block">Evite Dívidas</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <NavButtonWithTooltip
                icon={BookOpen}
                label="Investimentos"
                tooltip={{ text: 'Educação Financeira', color: 'text-blue-400' }}
                onClick={() => navigate('/investments')}
                isActive={activeTooltip === 0}
                testId="investments-nav-btn"
              />
              <NavButtonWithTooltip
                icon={Shield}
                label="Análise"
                tooltip={{ text: 'Analise sua Decisão', color: 'text-orange-400' }}
                onClick={() => navigate('/analyze')}
                isActive={activeTooltip === 1}
                testId="analyze-nav-btn"
              />
              <NavButtonWithTooltip
                icon={Crown}
                label="Planos"
                tooltip={{ text: 'Conheça os Planos', color: 'text-yellow-400' }}
                onClick={() => navigate('/pricing')}
                isActive={activeTooltip === 2}
                testId="pricing-nav-btn"
              />
              {user ? (
                <>
                  <PushNotifications user={user} />
                  <NavButtonWithTooltip
                    icon={User}
                    label="Histórico"
                    tooltip={{ text: 'Seu Perfil', color: 'text-purple-400' }}
                    onClick={() => navigate('/dashboard')}
                    isActive={activeTooltip === 3}
                    testId="dashboard-nav-btn"
                  />
                  <Button 
                    variant="ghost" 
                    className="text-gray-400 hover:text-white text-sm px-2"
                    onClick={logout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <NavButtonWithTooltip
                  icon={User}
                  label="Entrar"
                  tooltip={{ text: 'Seu Perfil', color: 'text-purple-400' }}
                  onClick={() => login()}
                  isActive={activeTooltip === 3}
                  testId="login-btn"
                />
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Trial Banner for logged in users - positioned below tooltips */}
      {user && trialInfo?.is_trial && (
        <motion.div 
          className="fixed top-[115px] sm:top-[130px] left-0 right-0 z-[45] bg-gradient-to-r from-amber-500/90 to-orange-500/90 border-b border-amber-400/50"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Clock className="w-4 h-4 text-white flex-shrink-0" />
              <span className="text-xs sm:text-sm text-white font-medium">
                <span className="hidden sm:inline">Teste grátis: </span>
                <strong>{trialInfo.trial_days_remaining} dias restantes</strong>
              </span>
            </div>
            <Button 
              size="sm" 
              className="bg-white text-amber-600 hover:bg-amber-50 text-xs px-3 py-1 h-auto font-bold rounded-full flex-shrink-0"
              onClick={() => navigate('/pricing')}
            >
              Upgrade
            </Button>
          </div>
        </motion.div>
      )}

      {/* Hero Section */}
      <section className={`relative min-h-screen flex items-start pt-32 sm:pt-36 lg:pt-24 lg:items-center ${user && trialInfo?.is_trial ? 'pt-40 sm:pt-44' : ''}`}>
        {/* Background glow */}
        <div className="absolute inset-0 hero-glow" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="grid lg:grid-cols-2 gap-12 items-center"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {/* Left content */}
            <div className="text-left">
              <motion.div variants={fadeInUp}>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] text-sm font-medium mb-6">
                  <Wallet className="w-4 h-4" />
                  {user && trialInfo?.access_type === 'premium' && !trialInfo?.is_trial 
                    ? 'Área Premium' 
                    : 'Organize suas Finanças'}
                </span>
              </motion.div>
              
              {/* Personalized title for Premium users */}
              {user && trialInfo?.access_type === 'premium' && !trialInfo?.is_trial ? (
                <>
                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6"
                    variants={fadeInUp}
                  >
                    Bem-vindo de volta,
                    <span className="block text-[#00FF94]">{user.name?.split(' ')[0] || 'Premium'}!</span>
                  </motion.h1>
                  
                  <motion.div 
                    className="mb-8 max-w-xl"
                    variants={fadeInUp}
                  >
                    <AnimatePresence mode="wait">
                      <motion.p 
                        key={currentInsightIndex}
                        className="text-lg text-gray-300 bg-[#0A0A0A]/50 border border-white/10 rounded-xl px-4 py-3"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {premiumInsights[currentInsightIndex] || 'Carregando seus insights...'}
                      </motion.p>
                    </AnimatePresence>
                    {premiumInsights.length > 1 && (
                      <div className="flex gap-1 mt-3 justify-center">
                        {premiumInsights.map((_, idx) => (
                          <div 
                            key={idx}
                            className={`w-2 h-2 rounded-full transition-colors ${idx === currentInsightIndex ? 'bg-[#00FF94]' : 'bg-white/20'}`}
                          />
                        ))}
                      </div>
                    )}
                  </motion.div>
                </>
              ) : (
                <>
                  <motion.h1 
                    className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-6"
                    variants={fadeInUp}
                  >
                    Organize suas Finanças com
                    <span className="block text-[#00FF94] drop-shadow-[0_0_15px_rgba(0,255,148,0.5)]">Inteligência Artificial</span>
                  </motion.h1>
                  
                  <motion.p 
                    className="text-lg text-gray-400 mb-8 max-w-xl"
                    variants={fadeInUp}
                  >
                    Preso em dívidas e não sabe como sair? Descubra o que está sumindo com o seu dinheiro em menos de 1 minuto. Totalmente anônimo e sem precisar de cadastro.
                  </motion.p>
                </>
              )}
              
              <motion.div className="flex flex-col gap-3 items-start" variants={fadeInUp}>
                {user && trialInfo && !trialInfo.is_trial && trialInfo.access_type === 'premium' && (
                  <span className="text-sm text-[#00FF94]">✓ Você é Premium</span>
                )}
                <motion.div 
                  className="relative group"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {/* Pulsing glow behind button */}
                  <div className="absolute inset-0 bg-[#00FF94]/30 rounded-2xl blur-xl animate-pulse group-hover:bg-[#00FF94]/40 transition-all" />
                  <Button 
                    size="lg"
                    className="relative bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-2xl font-black px-6 sm:px-10 py-6 sm:py-7 text-base sm:text-lg shadow-[0_0_30px_rgba(0,255,148,0.3)] hover:shadow-[0_0_50px_rgba(0,255,148,0.5)] transition-all border-2 border-[#00FF94]/50"
                    onClick={() => {
                      if (user && trialInfo && !trialInfo.is_trial && trialInfo.access_type === 'premium') {
                        navigate('/expenses');
                      } else {
                        setShowWizard(true);
                      }
                    }}
                    data-testid="hero-start-cta"
                  >
                    <Wallet className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                    {user && trialInfo && !trialInfo.is_trial && trialInfo.access_type === 'premium' 
                      ? 'Acessar Organizador' 
                      : 'Começar Análise Grátis'}
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
                    </motion.div>
                  </Button>
                </motion.div>
                <span className="text-xs sm:text-sm text-gray-500 ml-1">100% gratuito, sem cadastro</span>
                
                {/* WhatsApp button */}
                <a
                  href="https://wa.me/5511956023391?text=Ol%C3%A1%21%20Tenho%20uma%20d%C3%BAvida%20sobre%20o%20Evite%20D%C3%ADvidas."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group mt-2"
                >
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <span className="text-sm">Tem alguma dúvida? Fale comigo no WhatsApp.</span>
                </a>
              </motion.div>

              {/* Download App Button - Centered and Prominent */}
              <motion.div 
                className="w-full flex justify-center mt-6"
                variants={fadeInUp}
              >
                <DownloadAppButton />
              </motion.div>
            </div>

            {/* Right visual - hidden on mobile, visible on large screens */}
            <motion.div 
              className="relative hidden lg:block max-w-lg mx-auto"
              variants={fadeInUp}
            >
              <div className="relative w-full aspect-square">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00FF94]/20 to-transparent rounded-3xl" />
                <img 
                  src="https://images.unsplash.com/photo-1656332428317-d4532b0fe5aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHwzZCUyMHJlbmRlciUyMGdyZWVuJTIwcGFkbG9jayUyMGN5YmVyJTIwc2VjdXJpdHklMjBibGFjayUyMGJhY2tncm91bmR8ZW58MHx8fGdyZWVufDE3NjU4OTU0Nzd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Financial Protection"
                  className="w-full h-full object-cover rounded-3xl opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent rounded-3xl" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Como Funciona Section */}
      <section className="py-24 relative" id="como-funciona">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Como Funciona o Organizador
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Três passos simples para organizar suas finanças
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Registre suas despesas',
                description: 'Adicione seus gastos diários em categorias como Alimentação, Transporte, Lazer e mais. Leva menos de 1 minuto por despesa.',
                icon: <Wallet className="w-6 h-6" />
              },
              {
                step: '02',
                title: 'Acompanhe o resumo mensal',
                description: 'Veja para onde seu dinheiro está indo com gráficos claros. Descubra qual categoria consome mais do seu orçamento.',
                icon: <Zap className="w-6 h-6" />
              },
              {
                step: '03',
                title: 'Defina metas e economize',
                description: 'Estabeleça um limite mensal de gastos e receba alertas quando estiver perto de ultrapassar. Tome decisões mais conscientes.',
                icon: <Shield className="w-6 h-6" />
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-8 h-full card-hover relative overflow-hidden">
                  <div className="absolute top-0 right-0 text-8xl font-black text-white/5 -mr-4 -mt-4">
                    {item.step}
                  </div>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-[#00FF94]/10 flex items-center justify-center text-[#00FF94] mb-6">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-gray-400">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Números e Resultados Section */}
      <section className="py-24 relative bg-[#0A0A0A]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Resultados Reais
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Veja o impacto de organizar suas finanças
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { number: 'R$ 380', label: 'economia média mensal', sublabel: 'identificando gastos desnecessários', icon: PiggyBank },
              { number: '87%', label: 'dos usuários', sublabel: 'encontram onde cortar gastos no 1º mês', icon: TrendingUp },
              { number: '3 min', label: 'por dia', sublabel: 'é o tempo médio de uso', icon: Clock },
              { number: '10+', label: 'pessoas', sublabel: 'já organizaram suas finanças', icon: Users },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="bg-[#121212] border border-white/5 rounded-2xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-[#00FF94]/10 flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-[#00FF94]" />
                </div>
                <p className="text-3xl font-black text-[#00FF94] mb-1">{stat.number}</p>
                <p className="text-white font-medium">{stat.label}</p>
                <p className="text-sm text-gray-500">{stat.sublabel}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prova Social - Depoimentos */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              O que dizem nossos usuários
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Carla M.',
                role: 'Autônoma',
                text: 'Nunca imaginei que gastava tanto com delivery! Em 2 semanas já consegui economizar R$ 200 só cortando pedidos por impulso.',
                stars: 5
              },
              {
                name: 'Ricardo S.',
                role: 'CLT',
                text: 'O comparativo mensal do Premium me mostrou que meus gastos com transporte aumentaram 40%. Mudei de rota e economizo R$ 150/mês.',
                stars: 5
              },
              {
                name: 'Ana Paula',
                role: 'Estudante',
                text: 'Comecei no teste grátis e já vi valor. Assinei o Premium porque as categorias personalizadas me ajudam a controlar melhor.',
                stars: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">&ldquo;{testimonial.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#00FF94]/20 flex items-center justify-center">
                    <span className="text-[#00FF94] font-bold">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section className="py-24 relative bg-[#0A0A0A]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Por que usar?
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Evite decisoes impulsivas',
              'Reduza o risco de endividamento',
              'Linguagem simples e direta',
              'Análise imediata',
              'Sem falar com ninguem',
              'Custo baixo comparado ao prejuizo de errar'
            ].map((benefit, index) => (
              <motion.div
                key={benefit}
                className="flex items-center gap-4 p-4 rounded-xl bg-[#121212] border border-white/5"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-8 h-8 rounded-full bg-[#00FF94]/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-[#00FF94]" />
                </div>
                <span className="text-gray-300">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Education Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Novo
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Aprenda a Investir
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Além de proteger, ajudamos você a fazer seu dinheiro render de forma segura
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-8 card-hover cursor-pointer"
              onClick={() => navigate('/investments')}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#00FF94]/10 flex items-center justify-center text-[#00FF94] mb-6">
                <BookOpen className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Guia de Investimentos</h3>
              <p className="text-gray-400 mb-4">
                Aprenda sobre Poupança, CDB, Tesouro Direto e outros investimentos seguros para iniciantes.
              </p>
              <span className="text-[#00FF94] font-medium inline-flex items-center">
                Acessar guia <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </motion.div>

            <motion.div
              className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-8 card-hover cursor-pointer"
              onClick={() => navigate('/simulator')}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#00FF94]/10 flex items-center justify-center text-[#00FF94] mb-6">
                <Calculator className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Simulador de Rendimentos</h3>
              <p className="text-gray-400 mb-4">
                Calcule quanto seu dinheiro pode render em diferentes tipos de investimento.
              </p>
              <span className="text-[#00FF94] font-medium inline-flex items-center">
                Simular agora <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative bg-[#0A0A0A]/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Perguntas Frequentes
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                question: 'O teste grátis de 7 dias é realmente grátis?',
                answer: 'Sim! Você não precisa cadastrar cartão de crédito. São 7 dias completos para testar o organizador de despesas. Se gostar, pode assinar o Premium para continuar usando.'
              },
              {
                question: 'O que acontece depois dos 7 dias?',
                answer: 'Seus dados ficam salvos! Você pode assinar o Premium para continuar usando, ou suas informações permanecem guardadas caso decida assinar depois.'
              },
              {
                question: 'Qual a diferença entre o teste grátis e o Premium?',
                answer: 'No teste grátis você pode adicionar despesas e ver o resumo mensal. Com o Premium, você desbloqueia categorias personalizadas, comparativos dos últimos 6 meses, análises de tendência e alertas inteligentes.'
              },
              {
                question: 'Como funciona o comparativo mensal?',
                answer: 'O comparativo mostra a evolução dos seus gastos nos últimos 6 meses, categoria por categoria. Assim você identifica onde está gastando mais e pode agir. Disponível apenas no plano Premium.'
              },
              {
                question: 'Posso cancelar a assinatura quando quiser?',
                answer: 'Sim! Você pode cancelar a qualquer momento, sem multas ou taxas. Seu acesso continua até o fim do período pago.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="border border-white/10 rounded-xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <button
                  className="w-full p-5 flex items-center justify-between text-left bg-[#0A0A0A] hover:bg-[#121212] transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="text-white font-medium pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#00FF94] flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-5 pt-0 text-gray-400 bg-[#0A0A0A]">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* CTA after FAQ */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 mb-4">Ainda tem dúvidas?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-6"
                onClick={() => navigate('/expenses')}
              >
                Começar teste grátis
              </Button>
              <a
                href="https://wa.me/5511956023391?text=Ol%C3%A1%21%20Tenho%20uma%20d%C3%BAvida%20sobre%20o%20Evite%20D%C3%ADvidas."
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-6">
                  Falar no WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Download App Section */}
      <section className="py-24 relative bg-[#0A0A0A]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Leve o Evite Dívidas no Bolso
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Instale o app gratuitamente e tenha acesso rápido às análises financeiras
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <DownloadAppSection />
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Pronto para proteger seu dinheiro?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Um pequeno valor para evitar um grande erro financeiro.
            </p>
            <Button 
              size="lg"
              className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-10 py-6 text-lg glow-primary hover:scale-105 transition-all"
              onClick={() => navigate('/analyze')}
              data-testid="analyze-cta-btn-2"
            >
              Analisar minha decisão - R$ 9,90
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Aviso Legal */}
      <section className="py-12 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-6 rounded-xl bg-[#0A0A0A] border border-white/5">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-gray-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-white mb-2">Aviso Importante</h3>
                <p className="text-sm text-gray-500">
                  O Evite Dívidas não oferece consultoria financeira, não indica investimentos e não garante resultados. 
                  As análises são educativas e preventivas, com base em padrões financeiros comuns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Caixa de Sugestões */}
      <section className="py-16 border-t border-white/5 bg-gradient-to-b from-transparent to-[#0A0A0A]/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00FF94]/30 bg-[#00FF94]/5 mb-4">
              <MessageSquare className="w-4 h-4 text-[#00FF94]" />
              <span className="text-[#00FF94] text-sm font-medium">Sua opinião importa</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Tem uma sugestão?
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Ajude-nos a melhorar o Evite Dívidas. Envie suas ideias, críticas ou sugestões de novas funcionalidades.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-[#0A0A0A] rounded-2xl p-6 sm:p-8 border border-white/5"
          >
            <form onSubmit={handleSubmitSuggestion} className="space-y-4">
              <div>
                <textarea
                  value={suggestionText}
                  onChange={(e) => setSuggestionText(e.target.value)}
                  placeholder={user 
                    ? "Escreva sua sugestão aqui... (minimo 10 caracteres)" 
                    : "Faça login para enviar sua sugestão..."
                  }
                  disabled={!user || sendingSuggestion}
                  className="w-full h-32 px-4 py-3 bg-[#141414] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#00FF94]/50 focus:ring-1 focus:ring-[#00FF94]/20 resize-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  maxLength={2000}
                />
                <div className="flex justify-between items-center mt-2 text-sm">
                  <span className="text-gray-500">
                    {suggestionText.length}/2000 caracteres
                  </span>
                  {!user && (
                    <button 
                      type="button"
                      onClick={() => login()}
                      className="text-[#00FF94] hover:underline"
                    >
                      Fazer login para enviar
                    </button>
                  )}
                </div>
              </div>
              <Button
                type="submit"
                disabled={!user || sendingSuggestion || suggestionText.trim().length < 10}
                className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-xl font-semibold py-6 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {sendingSuggestion ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Enviar Sugestao
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 pb-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="text-gray-400">Evite Dívidas</span>
            </div>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate('/blog')} 
                className="text-sm text-gray-500 hover:text-[#00FF94] transition-colors"
              >
                Blog
              </button>
              <button 
                onClick={() => navigate('/pricing')} 
                className="text-sm text-gray-500 hover:text-[#00FF94] transition-colors"
              >
                Planos
              </button>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.instagram.com/evite_dividas_oficial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#E1306C] hover:border-[#E1306C]/30 hover:bg-[#E1306C]/10 transition-all"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a 
                href="https://br.pinterest.com/evitedividas/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#E60023] hover:border-[#E60023]/30 hover:bg-[#E60023]/10 transition-all"
                aria-label="Pinterest"
                data-testid="footer-pinterest-link"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>
              </a>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-600">
              Evite decisões financeiras que viram dívida.
            </p>
          </div>
        </div>
      </footer>
      <QuickNav />
      <OnboardingWizard open={showWizard} onClose={() => setShowWizard(false)} />
    </div>
  );
};

export default LandingPage;
